
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { ValidationObserver } from "vee-validate";
import Multiselect from "vue-multiselect";
import {
  IInformationOptionsEmployees,
  IPageResponse,
  ISolCustomerEmployeeParams,
  ISolCustomerEmployeeResponse,
  ISolCustomerVehicleParams,
  ISolCustomerVehicleResponse,
} from "@/services";
import { ICountry, ICountryConfigResponse, IUserClientData, IUserDetailData } from "@/entities";
import FirmsReportEmployeeSearchModal from "@/components/firms/FirmsReportEmployeeSearchModal.vue";
import {
  createSearchOption,
  IGroupDocumentType,
  IOptionDocumentType,
  SEARCH_TYPES,
} from "@/entities/documenttype";

@Component({
  name: "firms-search",
  components: { FirmsReportEmployeeSearchModal, ValidationObserver, Multiselect },
})
export default class FirmsSearch extends Vue {
  @Action("fetchEmployee") fetchEmployee!: (
    params: ISolCustomerEmployeeParams
  ) => Promise<ISolCustomerEmployeeResponse | undefined>;
  @Action("fetchVehicle") fetchVehicle!: (
    params: ISolCustomerVehicleParams
  ) => Promise<ISolCustomerVehicleResponse | undefined>;

  @Getter("getCountriesPage") countriesPage: IPageResponse<ICountry> | undefined;
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData | undefined;

  public countrySelected: ICountry | null = null;
  @Watch("countrySelected")
  async onCountrySelectedChange(country: ICountry | null): Promise<void> {
    this.configByCountry = null;
    this.optionsSearchTypeSelect = [];
    this.searchTypeSelected = null;

    const countryCode = country?.code || "";
    const config = await this.$services.country.getCountryConfigByCode(countryCode);
    if (config) {
      this.configByCountry = config;
      await this.loadOptionsSearchTypeSelect();
      this.disableExportEmployees = this.configByCountry.disabledExportEmployees!;
    }
  }
  public searchTypeSelected: { value: string; text: string } | undefined | null = null;
  public identifierInput: string | null = null;
  public solCustomerEmployee: ISolCustomerEmployeeResponse | undefined;
  public solCustomerVehicle: ISolCustomerVehicleResponse | undefined;
  visibleCountry = true;
  entities: string[] = [];
  configByCountry: ICountryConfigResponse | null = null;
  optionsSearchTypeSelect: IGroupDocumentType[] = [];
  public disableExportEmployees = false;

  async loadOptionsSearchTypeSelect(): Promise<void> {
    const config = this.configByCountry;
    const groupCrew = this.createCrewGroup();
    const groupFleet = this.createFleetGroup(config);

    this.optionsSearchTypeSelect = this.buildFinalOptions(groupCrew, groupFleet, config);
    this.searchTypeSelected = this.optionsSearchTypeSelect[0].options[0];
  }

  createCrewGroup(): IGroupDocumentType {
    const groupCrew: IGroupDocumentType = {
      label: this.$t("label.crew") as string,
      options: [],
    };

    // Tipos de documento
    groupCrew.options.push(...this.getDocumentTypeOptions());
    return groupCrew;
  }

  createFleetGroup(config: ICountryConfigResponse | null): IGroupDocumentType {
    const groupFleet: IGroupDocumentType = {
      label: this.$t("label.fleet") as string,
      options: [],
    };

    if (config?.enabledQueryFleetByInternalNumber) {
      groupFleet.options.push(
        createSearchOption(SEARCH_TYPES.FLEET.INTERNAL_NUMBER, this.$t.bind(this))
      );
    }

    if (config?.enabledQueryFleetByPlate) {
      groupFleet.options.push(createSearchOption(SEARCH_TYPES.FLEET.PLATE, this.$t.bind(this)));
    }

    return groupFleet;
  }

  getDocumentTypeOptions(): IOptionDocumentType[] {
    const documentTypes = this.countrySelected?.documentTypes || [];
    return documentTypes.map(
      (doc) =>
        ({
          value: doc.code || "",
          text: this.$te(`label.documentType.${doc.description.toLowerCase()}`)
            ? this.$t(`label.documentType.${doc.description.toLowerCase()}`)
            : doc.description || "",
        } as IOptionDocumentType)
    );
  }

  buildFinalOptions(
    groupCrew: IGroupDocumentType,
    groupFleet: IGroupDocumentType,
    config: ICountryConfigResponse | null
  ): IGroupDocumentType[] {
    const hasCrewOptions = groupCrew.options.length > 0;
    const hasFleetOptions = groupFleet.options.length > 0;

    if (hasCrewOptions && hasFleetOptions) {
      return [groupCrew, groupFleet];
    } else if (hasCrewOptions) {
      return [groupCrew];
    } else if (hasFleetOptions) {
      return [groupFleet];
    } else if (config) {
      return this.loadSearchTypeByDefault();
    }
    return [];
  }

  loadSearchTypeByDefault(): IGroupDocumentType[] {
    const groupCrew: IGroupDocumentType = {
      label: this.$t("label.crew") as string,
      options: this.getDocumentTypeOptions(),
    };

    const groupFleet: IGroupDocumentType = {
      label: this.$t("label.fleet") as string,
      options: [
        createSearchOption(SEARCH_TYPES.FLEET.INTERNAL_NUMBER, this.$t.bind(this)),
        createSearchOption(SEARCH_TYPES.FLEET.PLATE, this.$t.bind(this)),
      ],
    };

    return [groupCrew, groupFleet];
  }

  get informationOptionsEmployees(): IInformationOptionsEmployees {
    return {
      timeZone: this.getTimeZone(),
    } as IInformationOptionsEmployees;
  }

  get clients(): IUserClientData[] | [] {
    return this.getLoggedUser?.clients || [];
  }

  get optionsCountries(): ICountry[] {
    if (this.getLoggedUser?.admin) {
      this.visibleCountry = true;
      if (this.getLoggedUser?.userType == "CLIENTE") {
        return (
          this.countriesPage?.content.filter(
            (country) => this.getLoggedUser?.country?.id === country.id
          ) || []
        );
      }
      return this.countriesPage?.content || [];
    } else if (this.getLoggedUser?.country?.id) {
      this.visibleCountry = false;
      this.countrySelected = this.getLoggedUser.country;
      return (
        this.countriesPage?.content.filter(
          (country) => this.getLoggedUser?.country?.id === country.id
        ) || []
      );
    }
    return [];
  }

  mounted(): void {
    this.clients.forEach((client) => this.entities?.push(client.description || ""));
    this.countrySelected =
      this.optionsCountries.find((country) => country.id === this.getLoggedUser?.country?.id) ||
      null;
  }

  onChangeCountry(): void {
    if (this.searchTypeSelected) {
      this.searchTypeSelected =
        this.optionsSearchTypeSelect
          .flatMap((option) => option.options)
          .find((option) => option.value === this.searchTypeSelected?.value) ||
        this.optionsSearchTypeSelect[0].options[0];
    } else {
      if (this.optionsSearchTypeSelect.length > 0) {
        this.searchTypeSelected = this.optionsSearchTypeSelect[0].options[0];
      }
    }
  }

  async onSubmit(): Promise<void> {
    this.$emit("search", this.searchTypeSelected?.value);

    const plateValue = this.$t(SEARCH_TYPES.FLEET.PLATE.value);
    const internalNumberValue = this.$t(SEARCH_TYPES.FLEET.INTERNAL_NUMBER.value);

    if (this.isFleetSearch(plateValue as string, internalNumberValue as string)) {
      await this.searchVehicle();
    } else {
      await this.searchEmployee();
    }
  }

  private isFleetSearch(plateValue: string, internalNumberValue: string): boolean {
    return (
      this.searchTypeSelected?.value === plateValue ||
      this.searchTypeSelected?.value === internalNumberValue
    );
  }

  async searchEmployee(): Promise<void> {
    this.solCustomerEmployee = await this.fetchEmployee({
      countryCode: this.countrySelected!.code!,
      documentTypeCode: this.searchTypeSelected?.value,
      value: this.identifierInput!,
    });

    if (this.solCustomerEmployee !== undefined && !this.solCustomerEmployee?.success) {
      Vue.swal({
        title: this.solCustomerEmployee?.errors?.length
          ? this.solCustomerEmployee?.errors[0]?.errorMensaje
          : this.$t("error.notfound.employee"),
        text: "Por favor, revise los datos introducidos.",
        icon: "warning",
      });
    }

    this.$emit("search-employee", {
      searchType: this.countrySelected?.documentTypes?.find(
        (doc) => doc.code === this.searchTypeSelected?.value
      )?.description,
      employee: this.solCustomerEmployee,
      country: this.countrySelected?.code,
    });

    this.$emit("search-vehicle", undefined);
  }

  async searchVehicle(): Promise<void> {
    const plateValue = this.$t(SEARCH_TYPES.FLEET.PLATE.value);
    const internalNumberValue = this.$t(SEARCH_TYPES.FLEET.INTERNAL_NUMBER.value);

    this.solCustomerVehicle = await this.fetchVehicle({
      countryCode: this.countrySelected!.code!,
      plate: this.searchTypeSelected?.value === plateValue ? this.identifierInput! : undefined,
      code:
        this.searchTypeSelected?.value === internalNumberValue ? this.identifierInput! : undefined,
    });

    if (!this.solCustomerVehicle || !this.solCustomerVehicle.success) {
      Vue.swal({
        title: this.$t("error.notfound.vehicle"),
        text: "Por favor, revise los datos introducidos.",
        icon: "warning",
      });
    }

    this.$emit("search-vehicle", this.solCustomerVehicle);
    this.$emit("search-employee", undefined, undefined);
  }

  async openModalOrExecuteReportEmployeeSearch(): Promise<void> {
    const firmsReportEmployeeSearchModal = this.$refs
      .firmsReportEmployeeSearchModal as FirmsReportEmployeeSearchModal;
    firmsReportEmployeeSearchModal.$emit("showFirmsReportEmployeeSearchModal");
  }

  private getTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}
